import { type FC } from "react";

import { Clock } from "lucide-react";

import { Tag } from "@/shared/components/ui/tag";
import { cn } from "@/shared/utils/core";

export interface SearchCategory {
  icon: FC<{ className?: string }>;
  title: string;
  description: string;
  time: string;
  comingSoon?: boolean;
  iconBg: string;
  iconColor: string;
}

interface SearchCategoryCardProps {
  category: SearchCategory;
  isSearching?: boolean;
}

export const SearchCategoryCard: FC<SearchCategoryCardProps> = ({ category, isSearching }) => {
  const IconComponent = category.icon;

  const timeLabel = category.comingSoon
    ? null
    : isSearching
      ? category.time.replace("Takes about", "In progress, takes about")
      : category.time;

  return (
    <div className="bg-white border border-primary rounded-lg p-6 flex flex-col justify-between gap-4 min-h-[192px]">
      <div className="flex gap-4 items-start">
        <div
          className={cn(
            "shrink-0 size-10 rounded-sm flex items-center justify-center",
            category.iconBg,
          )}
        >
          <IconComponent className={category.iconColor} />
        </div>
        <div className="flex flex-col gap-0 min-w-0">
          <p className="text-base font-medium text-primary tracking-[-0.32px]">{category.title}</p>
          <p className="text-sm text-secondary">{category.description}</p>
        </div>
      </div>

      {category.comingSoon ? (
        <div>
          <Tag color="stone" size="sm">
            Coming soon
          </Tag>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {isSearching ? (
            <span
              className="relative flex size-[14px] shrink-0 items-center justify-center"
              aria-hidden="true"
            >
              <span className="absolute size-2 animate-ping rounded-full bg-brand opacity-60" />
              <span className="relative size-2 rounded-full bg-brand" />
            </span>
          ) : (
            <Clock size={14} className="shrink-0 text-tertiary" />
          )}
          <p className="text-xs text-tertiary leading-[1.55]">{timeLabel}</p>
        </div>
      )}
    </div>
  );
};
